// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { RotateCcw } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RestoreDefaultDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Display name of the setting being restored */
  settingName?: string;
  /** Default value to show (optional) */
  defaultValue?: string;
  /** Additional disabled condition for the confirm button */
  confirmDisabled?: boolean;
}

export function RestoreDefaultDialog({
  open,
  onClose,
  onConfirm,
  settingName,
  defaultValue,
  confirmDisabled,
}: RestoreDefaultDialogProps) {
  const { t } = useTranslation("back-office");
  const lastNameRef = useRef(settingName);
  if (settingName) lastNameRef.current = settingName;
  const displayName = settingName || lastNameRef.current;

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent id="dialog-restore-setting" className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane icon={RotateCcw} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("settings.list.restoreDialog.title")}</DialogTitle>
              <DialogDescription>
                {t("settings.list.restoreDialog.description", { name: displayName })}
              </DialogDescription>
            </DialogHeader>
            {defaultValue !== undefined && (
              <div className="text-sm">
                <span className="text-muted-foreground">Default value: </span>
                <code className="rounded bg-muted px-2 py-1">{defaultValue}</code>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("settings.list.restoreDialog.cancel")}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={confirmDisabled}>
            <RotateCcw className="h-4 w-4" />
            {t("settings.list.restoreDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
